import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'spassu-code:cockpit:metrics:';

/**
 * Remembers whether the reader keeps the metrics details open. `defaultExpanded`
 * is the state used when nothing valid is stored: the details region of the
 * metrics bar starts closed (RN-COCK-03), while a consumer that wants the old
 * open-by-default behaviour keeps the default.
 */
export function useMetricsSectionPreference(
  scope?: string | null,
  defaultExpanded = true,
) {
  const [expanded, setExpanded] = useState(() =>
    readPreference(scope, defaultExpanded),
  );

  useEffect(
    () => setExpanded(readPreference(scope, defaultExpanded)),
    [scope, defaultExpanded],
  );

  const updateExpanded = useCallback(
    (next: boolean) => {
      setExpanded(next);
      if (!scope) return;
      try {
        window.localStorage.setItem(
          `${STORAGE_PREFIX}${scope}`,
          next ? 'expanded' : 'collapsed',
        );
      } catch {
        // Preference storage is optional; the current accessible state remains usable.
      }
    },
    [scope],
  );

  return { expanded, setExpanded: updateExpanded };
}

function readPreference(
  scope: string | null | undefined,
  defaultExpanded: boolean,
): boolean {
  if (!scope) return defaultExpanded;
  try {
    const stored = window.localStorage.getItem(`${STORAGE_PREFIX}${scope}`);
    if (stored === 'expanded') return true;
    if (stored === 'collapsed') return false;
    return defaultExpanded;
  } catch {
    return defaultExpanded;
  }
}
